/**
 * Neal Avionics: Garmin G3X Touch Panel Builder — Phase 1
 *
 * Plain ES module loaded from sections/neal-g3x-panel-builder.liquid.
 * Deliberately NOT part of the Horizon importmap — no imports from
 * theme modules, no external libraries.
 *
 * The <neal-g3x-builder> custom element owns all behavior:
 * - step navigation (6 steps)
 * - aircraft-profile state
 * - display selection with quantity limits
 * - inline-SVG panel preview population
 * - visual-only drag/tap/keyboard display arrangement (left/center/right)
 * - sticky configuration summary + review recap
 *
 * The visual arrangement NEVER changes quantities, part numbers, or any
 * equipment data — it only decides where already-selected units render.
 *
 * Safe for Shopify Theme Editor section reloads: the module executes
 * once per page, the element re-initializes itself per instance, and
 * all listeners and DOM lookups are scoped to the element (no document
 * or window listeners).
 */

const TAG_NAME = 'neal-g3x-builder';
const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';

/* Panel-preview geometry (must match the viewBox and panel path in
   snippets/neal-g3x-builder-panel-preview.liquid). */
const PANEL = {
  centerX: 600,
  rowCenterY: 270,
  maxRowWidth: 980,
  gap: 34,
};

/* Finished display artwork (Shopify CDN PNG) used as the complete visible
   unit, plus its rendered size in SVG units. Height is unchanged from the
   previous hand-drawn art so the panel composition holds; width follows each
   PNG's natural aspect ratio (460: 248×161 landscape, 470: 118×170 portrait)
   so nothing is stretched or cropped. The <image> also sets
   preserveAspectRatio as a belt-and-suspenders guard against distortion. */
const DISPLAY_ART = {
  gdu460: {
    image: 'https://cdn.shopify.com/s/files/1/0801/2120/0872/files/screen-g3x-touch-builder-large.png?v=1784812751',
    width: 317,
    height: 206,
  },
  gdu470: {
    image: 'https://cdn.shopify.com/s/files/1/0801/2120/0872/files/scrren-small-g3x-touch.png?v=1784812751',
    width: 153,
    height: 220,
  },
};

/* Inset of the arrangement swap-highlight box around each mounted unit, in
   SVG units — shown only while arranging. No recess, frame, or shell is drawn
   behind a unit at rest; the finished PNG carries its own bezel. */
const SWAP_INSET_X = 9;
const SWAP_INSET_Y = 12;

/* Visual arrangement slots, in panel order. Empty slots reserve a
   nominal width in the row so units keep positional meaning. */
const SLOTS = ['left', 'center', 'right'];
const EMPTY_SLOT_WIDTH = 170;
const DRAG_THRESHOLD = 5;

/* Step navigation re-anchors the viewport so the newly active step's header
   ("Step X of 5" + its h2) lands just below the site header, near the top.
   STEP_SCROLL_GAP is the breathing room under the header; SITE_HEADER_SELECTORS
   are the theme's own sticky header wrappers, measured live so the offset adapts
   to the desktop/mobile header rows without a hard-coded height. */
const STEP_SCROLL_GAP = 20;
const SITE_HEADER_SELECTORS = ['.header-section', '#header-component', '#header-group'];

/* PFD-left convention for default layouts and summary listings. */
const PANEL_ORDER = ['gdu460', 'gdu470'];

const FALLBACK_DATA = {
  constraints: { maxPerDisplay: 2, maxTotalDisplays: 3 },
  displays: [],
  steps: [],
  coreSystem: null,
  optionGroups: [],
  engine: null,
};

const AIRCRAFT_EQUIPMENT_LABELS = {
  navigator: 'Navigator',
  autopilot: 'Autopilot',
  audioPanel: 'Audio panel',
  engineMonitoring: 'Engine monitoring',
};

const AIRCRAFT_KEYS = [
  'make',
  'model',
  'year',
  'engines',
  'cylinders',
  'navigator',
  'autopilot',
  'audioPanel',
  'engineMonitoring',
];

/* The eight aircraft-profile fields required before Step 1 can continue.
   Aircraft year is intentionally NOT required. Single source of truth for the
   Continue button, the summary CTA, and Step-1 progress completion. */
const REQUIRED_AIRCRAFT_KEYS = [
  'make',
  'model',
  'engines',
  'cylinders',
  'navigator',
  'autopilot',
  'audioPanel',
  'engineMonitoring',
];

/* The builder persists only for the current browser tab/session: it uses
   sessionStorage (wiped when the tab or browser session closes), NOT
   localStorage (which kept a build on the device indefinitely). Bump
   STORAGE_VERSION when the saved state shape changes so stale saves are
   ignored on load. */
const STORAGE_VERSION = 1;
const STORAGE_KEY = `neal-g3x-builder.v${STORAGE_VERSION}`;
/* Obsolete localStorage key from before the move to sessionStorage. Removed
   once on load so an old device-saved build can never reappear. */
const LEGACY_LOCAL_KEY = 'neal-g3x-builder.v1';

/* Public Garmin LIST prices, in integer CENTS, from the July 2026 G3X Touch for
   Certificated Aircraft catalog. LIST (customer-facing) only — never dealer cost
   or MAP. Integer cents so currency math never uses floating-point dollars. Kit
   sub-components (GSU 25D, GMU 11, connector kits, etc.) are intentionally absent
   because their cost is bundled into the parent kit's list price. */
const G3X_LIST_PRICES_CENTS = {
  '010-02318-00': 679500, // GDU 470
  '010-02319-00': 899500, // GDU 460

  '010-02305-00': 400000, // Certified LRU Kit
  'K11-00038-00': 20000, // Certified Install Kit

  '010-01076-31': 240000, // GTR 20
  '010-01471-13': 255000, // GMA 245R
  '010-01074-71': 140000, // GAP 26
  '010-10040-01': 52000, // GA 56
  '010-01431-01': 11000, // 8 GB Data Card
  '013-00235-00': 40000, // GA 35
  '013-00244-00': 46500, // GA 36
  '010-12444-10': 26500, // GPS Antenna, BNC
  '010-01172-21': 87500, // GAD 29D
  '011-03271-00': 10000, // GAD 29 Connector Kit
  '010-01525-11': 82500, // GAD 27
  '011-03877-01': 16000, // GAD 27 Connector Kit
  '010-01561-15': 115000, // GDL 50R
  '010-01561-55': 100000, // GDL 51R
  '010-01561-35': 157500, // GDL 52R
  '011-04170-00': 8500, // GDL 5XR Connector Kit
  '190-02472-00': 21500, // Printed Pilot's Guide

  '010-02770-11': 230000, // GEA 24B
  'K00-01010-11': 123000, // 4-cylinder engine sensor kit
  'K00-01011-11': 161500, // 6-cylinder engine sensor kit

  '011-05783-00': 32000, // GPT 30A pressure sensor
  '011-05783-10': 32000, // GPT 15G pressure sensor
  '011-05783-20': 32000, // GPT 75G pressure sensor
  '011-05783-30': 32000, // GPT 150G pressure sensor
  '494-10001-00': 52000, // FT-60 fuel-flow transducer
  '494-10001-01': 52000, // FT-90 fuel-flow transducer
  '494-70001-00': 16000, // EGT thermocouple
  '494-70002-00': 12000, // TIT thermocouple
  '494-70008-00': 16000, // CHT thermocouple
  '494-70008-01': 6000, // CHT adapter thermocouple
  '494-70009-00': 42000, // Oil-temperature sensor
  '494-70010-00': 42000, // Temperature probe
};

function capitalize(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

class NealG3xBuilder extends HTMLElement {
  connectedCallback() {
    if (!this.initialized) {
      this.initialized = true;

      this.config = this.readData();
      this.state = this.defaultState();
      for (const display of this.config.displays) {
        this.state.quantities[display.id] = 0;
      }
      /* Drop any obsolete localStorage build from the old device-scoped
         builder, then overlay the current tab's sessionStorage build (both
         safe if storage is unavailable). */
      this.purgeLegacyLocalStorage();
      this.loadPersisted();

      this.dragState = null;
      this.lastRow = null;
      this.submitting = false;

      this.addEventListener('click', (event) => this.onClick(event));
      this.addEventListener('input', (event) => this.onInput(event));
      this.addEventListener('change', (event) => this.onChange(event));
      this.addEventListener('submit', (event) => this.onSubmit(event));
      this.addEventListener('keydown', (event) => this.onKeyDown(event));
      this.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.addEventListener('pointercancel', (event) => this.onPointerCancel(event));

      this.stampQuoteFormStart();

      /* Mirror any restored aircraft-profile values into the form inputs (state
         is the source of truth; this only reflects it into the DOM once). */
      this.syncAircraftInputsFromState();
    }

    this.render();
  }

  defaultState() {
    return {
      step: 1,
      aircraft: {},
      quantities: {},
      /* Visual-only arrangement — never feeds equipment data. */
      layout: { left: null, center: null, right: null },
      layoutCustomized: false,
      arrangeUnitId: null,
      /* Equipment selection (Steps 2–4). The certified core system is
         always required/included, so it needs no mutable flag. */
      selectedOptions: {}, // { optionId: true } — Step 3
      engineMonitoring: 'none', // 'none' | 'add' — Step 4 primary choice
      selectedSensors: {}, // { sensorId: true } — Step 4 additional sensors
    };
  }

  /* ------------------------------------------------------------------
     Persistence (per-tab sessionStorage; never throws)
     ------------------------------------------------------------------ */

  /* Single source of truth for the storage area. sessionStorage keeps a build
     for the current tab/session only and clears it when the tab or browser
     session closes. Reading this property can throw in locked-down contexts,
     so every caller wraps the access in try/catch. */
  sessionStore() {
    return window.sessionStorage;
  }

  /* One-time cleanup of the obsolete device-scoped build. The builder used to
     persist to localStorage, which left an old aircraft profile on the device
     for days or weeks. Remove only the builder's own legacy key — unrelated
     site storage is untouched — and never read it back, so stale builds can't
     reappear. Safe (no-op) if localStorage is blocked or already empty. */
  purgeLegacyLocalStorage() {
    try {
      window.localStorage.removeItem(LEGACY_LOCAL_KEY);
    } catch (error) {
      /* localStorage unavailable — nothing to purge. */
    }
  }

  loadPersisted() {
    let raw = null;
    try {
      raw = this.sessionStore().getItem(STORAGE_KEY);
    } catch (error) {
      return; // storage unavailable (private mode, disabled) — carry on
    }
    if (!raw) return;

    let parsed = null;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      this.clearPersisted();
      return;
    }

    if (!parsed || parsed.v !== STORAGE_VERSION || !parsed.state || typeof parsed.state !== 'object') {
      this.clearPersisted(); // invalid or outdated — discard
      return;
    }

    this.applyPersisted(parsed.state);
  }

  /* Overlay a saved snapshot onto default state, sanitizing every field
     against the current catalog so stale ids can never sneak in. */
  applyPersisted(saved) {
    const s = this.state;

    const step = Number.parseInt(saved.step, 10);
    if (step >= 1 && step <= 5) s.step = step;

    if (saved.aircraft && typeof saved.aircraft === 'object') {
      for (const key of AIRCRAFT_KEYS) {
        if (typeof saved.aircraft[key] === 'string') s.aircraft[key] = saved.aircraft[key];
      }
    }

    if (saved.quantities && typeof saved.quantities === 'object') {
      const maxPer = this.config.constraints.maxPerDisplay;
      for (const display of this.config.displays) {
        const qty = Number.parseInt(saved.quantities[display.id], 10);
        if (Number.isFinite(qty) && qty >= 0) {
          s.quantities[display.id] = Math.min(qty, maxPer);
        }
      }
    }

    if (saved.layout && typeof saved.layout === 'object') {
      for (const slot of SLOTS) {
        s.layout[slot] = typeof saved.layout[slot] === 'string' ? saved.layout[slot] : null;
      }
      s.layoutCustomized = Boolean(saved.layoutCustomized);
    }

    if (saved.selectedOptions && typeof saved.selectedOptions === 'object') {
      for (const option of this.allOptions()) {
        if (saved.selectedOptions[option.id]) s.selectedOptions[option.id] = true;
      }
    }

    if (saved.engineMonitoring === 'add' || saved.engineMonitoring === 'none') {
      s.engineMonitoring = saved.engineMonitoring;
    }

    if (saved.selectedSensors && typeof saved.selectedSensors === 'object') {
      for (const sensor of this.engineSensors()) {
        if (saved.selectedSensors[sensor.id]) s.selectedSensors[sensor.id] = true;
      }
    }
    /* reconcileLayout() in render() validates layout against quantities. */
  }

  persist() {
    try {
      const s = this.state;
      const snapshot = {
        step: s.step,
        aircraft: s.aircraft,
        quantities: s.quantities,
        layout: s.layout,
        layoutCustomized: s.layoutCustomized,
        selectedOptions: s.selectedOptions,
        engineMonitoring: s.engineMonitoring,
        selectedSensors: s.selectedSensors,
      };
      this.sessionStore().setItem(STORAGE_KEY, JSON.stringify({ v: STORAGE_VERSION, state: snapshot }));
    } catch (error) {
      /* Storage unavailable or full — persistence is best-effort. */
    }
  }

  clearPersisted() {
    try {
      this.sessionStore().removeItem(STORAGE_KEY);
    } catch (error) {
      /* Nothing to do. */
    }
  }

  startOver() {
    const confirmed =
      typeof window.confirm !== 'function' ||
      window.confirm('Start a new build? This clears your current configuration.');
    if (!confirmed) return;

    this.state = this.defaultState();
    for (const display of this.config.displays) this.state.quantities[display.id] = 0;
    this.submitting = false;

    this.resetQuoteUi();
    this.syncAircraftInputsFromState(); // clear the profile inputs to match the reset state
    this.render();
    this.style.paddingBottom = ''; // drop any Step-4 scroll padding left by the prior build
    this.clearPersisted(); // render() re-persists; remove afterward so nothing lingers

    const heading = this.querySelector('[data-step-panel="1"] .neal-g3x-builder__step-title');
    if (heading && typeof heading.focus === 'function') heading.focus();
    this.announce('Started a new build. Your previous configuration was cleared.');
  }

  disconnectedCallback() {
    /* All listeners live on this element, so there is nothing global to
       remove — just drop any in-flight drag so a Theme Editor section
       reload starts clean. */
    this.dragState = null;
  }

  readData() {
    const script = this.querySelector('script[data-neal-g3x-data]');
    if (!script) return FALLBACK_DATA;

    try {
      const parsed = JSON.parse(script.textContent);
      return {
        ...FALLBACK_DATA,
        ...parsed,
        constraints: { ...FALLBACK_DATA.constraints, ...(parsed.constraints || {}) },
      };
    } catch (error) {
      console.warn('[neal-g3x-builder] Could not parse configuration data.', error);
      return FALLBACK_DATA;
    }
  }

  getDisplay(id) {
    return this.config.displays.find((display) => display.id === id) || null;
  }

  totalDisplayCount() {
    return Object.values(this.state.quantities).reduce((sum, qty) => sum + qty, 0);
  }

  stepTitle(step) {
    const entry = this.config.steps.find((item) => item.id === step);
    if (entry) return entry.title;
    const heading = this.querySelector(`[data-step-panel="${step}"] .neal-g3x-builder__step-title`);
    return heading ? heading.textContent.trim() : `Step ${step}`;
  }

  /* ------------------------------------------------------------------
     Visual unit instances + layout reconciliation
     ------------------------------------------------------------------ */

  desiredUnits() {
    const units = [];
    for (const id of PANEL_ORDER) {
      const qty = this.state.quantities[id] || 0;
      for (let i = 1; i <= qty; i += 1) units.push(`${id}-${i}`);
    }
    return units;
  }

  unitType(unitId) {
    return unitId.slice(0, unitId.lastIndexOf('-'));
  }

  unitNumber(unitId) {
    return unitId.slice(unitId.lastIndexOf('-') + 1);
  }

  unitLabel(unitId) {
    const type = this.unitType(unitId);
    const display = this.getDisplay(type);
    const name = display ? display.name : type;
    const multiple = (this.state.quantities[type] || 0) > 1;
    return multiple ? `${name} (unit ${this.unitNumber(unitId)})` : name;
  }

  slotOfUnit(unitId) {
    return SLOTS.find((slot) => this.state.layout[slot] === unitId) || null;
  }

  /* Keep the visual layout in sync with quantities. Never the other
     way around — arrangement cannot alter what was selected. */
  reconcileLayout() {
    const desired = this.desiredUnits();
    const layout = this.state.layout;

    for (const slot of SLOTS) {
      if (layout[slot] && !desired.includes(layout[slot])) layout[slot] = null;
    }

    if (!this.state.layoutCustomized) {
      const fresh = { left: null, center: null, right: null };
      if (desired.length === 1) {
        fresh.center = desired[0];
      } else if (desired.length === 2) {
        fresh.left = desired[0];
        fresh.right = desired[1];
      } else if (desired.length >= 3) {
        fresh.left = desired[0];
        fresh.center = desired[1];
        fresh.right = desired[2];
      }
      this.state.layout = fresh;
    } else {
      const placed = new Set(SLOTS.map((slot) => layout[slot]).filter(Boolean));
      for (const unit of desired) {
        if (placed.has(unit)) continue;
        const free = ['center', 'left', 'right'].find((slot) => !layout[slot]);
        if (!free) break;
        layout[free] = unit;
        placed.add(unit);
      }
    }

    if (this.state.arrangeUnitId && !desired.includes(this.state.arrangeUnitId)) {
      this.state.arrangeUnitId = null;
    }
  }

  /* Move a unit to a slot; occupied slots swap. Visual only. */
  moveUnit(unitId, targetSlot, { keepSelection = false } = {}) {
    const layout = this.state.layout;
    const fromSlot = this.slotOfUnit(unitId);

    if (!fromSlot || !SLOTS.includes(targetSlot) || fromSlot === targetSlot) {
      this.render();
      return;
    }

    const displaced = layout[targetSlot] || null;
    layout[targetSlot] = unitId;
    layout[fromSlot] = displaced;
    this.state.layoutCustomized = true;
    if (!keepSelection) this.state.arrangeUnitId = null;

    this.render();

    let message = `${this.unitLabel(unitId)} moved to the ${targetSlot} position`;
    if (displaced) message += `, swapping with ${this.unitLabel(displaced)}`;
    this.announce(`${message}. Visual arrangement only — your equipment selection is unchanged.`);
  }

  onUnitTap(unitId) {
    const active = this.state.arrangeUnitId;

    if (active && active !== unitId) {
      this.moveUnit(active, this.slotOfUnit(unitId));
      return;
    }

    this.state.arrangeUnitId = active === unitId ? null : unitId;
    this.render();

    if (this.state.arrangeUnitId) {
      this.announce(
        `${this.unitLabel(unitId)} selected for arranging. Choose left, center, or right — or press Escape to cancel.`
      );
    } else {
      this.announce('Arrangement selection cancelled.');
    }
  }

  cancelArrange() {
    const previous = this.state.arrangeUnitId;
    this.state.arrangeUnitId = null;
    this.render();

    if (previous) {
      const el = this.querySelector(`[data-unit="${previous}"]`);
      if (el && typeof el.focus === 'function') el.focus();
    }
    this.announce('Arrangement selection cancelled.');
  }

  /* ------------------------------------------------------------------
     Events
     ------------------------------------------------------------------ */

  onClick(event) {
    /* A part-number link opens Neal Avionics' product search in a new tab — let
       the browser handle it and do nothing else: never toggle an option/sensor,
       add/remove a display, change quantity, change the step, call goToStep()/
       scrollActiveStepToTop(), start a drag/arrangement, or fire the summary CTA.
       No preventDefault (the link must open). Covers SKUs that sit inside a
       selectable option/sensor button, so those cards don't toggle. */
    const productLink = event.target.closest && event.target.closest('.neal-g3x-builder__part-link');
    if (productLink && this.contains(productLink)) return;

    /* Panel-svg interactions are handled by the pointer handlers. */
    if (event.target.closest && event.target.closest('[data-panel-svg]')) return;

    if (event.target.closest('[data-start-over]') || event.target.closest('[data-quote-restart]')) {
      this.startOver();
      return;
    }

    const arrangeButton = event.target.closest('[data-arrange-slot]');
    if (arrangeButton && this.contains(arrangeButton)) {
      if (this.state.arrangeUnitId) {
        this.moveUnit(this.state.arrangeUnitId, arrangeButton.dataset.arrangeSlot);
      }
      return;
    }

    if (event.target.closest('[data-arrange-cancel]')) {
      this.cancelArrange();
      return;
    }

    /* Configuration Summary CTA: navigate to — or, when its target is already the
       active step, re-scroll to — the step it represents. Handled before the
       generic step-target branch because goToStep() early-returns when the target
       equals the current step, which would leave a same-step CTA (e.g. "Add
       Aircraft Profile" while on Step 1) doing nothing. Reuses the existing step
       scroll logic and landing positions. */
    const summaryCta = event.target.closest('[data-summary-cta]');
    if (summaryCta && this.contains(summaryCta)) {
      event.preventDefault();
      /* Disabled "Select Displays" gate (no displays yet): do nothing — no
         scroll, no step change, no goToStep/scrollActiveStepToTop. Native
         `disabled` already suppresses the click; this is an explicit guard. */
      if (summaryCta.disabled) return;
      const target = Number.parseInt(summaryCta.dataset.stepTarget, 10);
      if (target >= 1 && target <= 5) {
        if (target === this.state.step) this.scrollActiveStepToTop(target);
        else this.goToStep(target);
      }
      return;
    }

    const stepButton = event.target.closest('[data-step-target]');
    if (stepButton && this.contains(stepButton)) {
      /* These step-nav controls are type="button"; preventDefault guards against
         any default activation before we drive the step change ourselves. */
      event.preventDefault();
      const target = Number.parseInt(stepButton.dataset.stepTarget, 10);
      if (target >= 1 && target <= 6) this.goToStep(target);
      return;
    }

    const optionToggle = event.target.closest('[data-option-toggle]');
    if (optionToggle && this.contains(optionToggle)) {
      this.toggleOption(optionToggle.dataset.optionToggle);
      return;
    }

    const sensorToggle = event.target.closest('[data-sensor-toggle]');
    if (sensorToggle && this.contains(sensorToggle)) {
      this.toggleSensor(sensorToggle.dataset.sensorToggle);
      return;
    }

    const engineMode = event.target.closest('[data-engine-mode]');
    if (engineMode && this.contains(engineMode)) {
      this.setEngineMode(engineMode.dataset.engineMode);
      return;
    }

    const qtyButton = event.target.closest('[data-qty-action]');
    if (qtyButton && this.contains(qtyButton)) {
      this.onQuantityAction(qtyButton);
    }
  }

  /* ------------------------------------------------------------------
     Equipment selection (Steps 2–4) — never touches display quantities
     ------------------------------------------------------------------ */

  toggleOption(id) {
    if (!id) return;
    const option = this.optionById(id);
    if (this.state.selectedOptions[id]) {
      delete this.state.selectedOptions[id];
    } else {
      this.state.selectedOptions[id] = true;
    }
    this.render();
    if (option) {
      this.announce(
        `${option.name} ${this.state.selectedOptions[id] ? 'added to' : 'removed from'} your options. Subject to Neal Avionics verification.`
      );
    }
  }

  toggleSensor(id) {
    if (!id) return;
    const sensor = this.sensorById(id);
    if (this.state.selectedSensors[id]) {
      delete this.state.selectedSensors[id];
    } else {
      this.state.selectedSensors[id] = true;
    }
    this.render();
    if (sensor) {
      this.announce(
        `${sensor.name} ${this.state.selectedSensors[id] ? 'added' : 'removed'}. Sensor quantities are verified by Neal Avionics.`
      );
    }
  }

  setEngineMode(mode) {
    if (mode !== 'none' && mode !== 'add') return;
    if (this.state.engineMonitoring === mode) return;
    this.state.engineMonitoring = mode;
    this.render();
    this.announce(
      mode === 'add'
        ? 'G3X engine monitoring added. The GEA 24B interface is included; sensor selections are preliminary.'
        : 'G3X engine monitoring not added.'
    );
  }

  /* ------------------------------------------------------------------
     Catalog helpers (data lives in neal-g3x-builder-data.liquid)
     ------------------------------------------------------------------ */

  optionGroups() {
    return Array.isArray(this.config.optionGroups) ? this.config.optionGroups : [];
  }

  allOptions() {
    const out = [];
    for (const group of this.optionGroups()) {
      for (const item of group.items || []) out.push(item);
    }
    return out;
  }

  optionById(id) {
    return this.allOptions().find((item) => item.id === id) || null;
  }

  selectedOptionList() {
    return this.allOptions().filter((item) => this.state.selectedOptions[item.id]);
  }

  engineData() {
    return this.config.engine || null;
  }

  engineSensors() {
    const engine = this.engineData();
    return engine && Array.isArray(engine.sensors) ? engine.sensors : [];
  }

  sensorById(id) {
    return this.engineSensors().find((sensor) => sensor.id === id) || null;
  }

  selectedSensorList() {
    return this.engineSensors().filter((sensor) => this.state.selectedSensors[sensor.id]);
  }

  /* Cylinder count parsed from the aircraft profile — never guessed. */
  cylinderCount() {
    const raw = String(this.state.aircraft.cylinders || '');
    if (raw.indexOf('4') !== -1) return '4';
    if (raw.indexOf('6') !== -1) return '6';
    return null;
  }

  recommendedSensorKit() {
    const engine = this.engineData();
    const cyl = this.cylinderCount();
    if (!engine || !engine.sensorKits || !cyl) return null;
    return engine.sensorKits[cyl] || null;
  }

  /* Connector kits whose primary is selected but the connector isn't —
     surfaced as recommendations only; dependencies are never enforced. */
  recommendedCompanions() {
    return this.allOptions().filter(
      (item) =>
        item.companionFor &&
        this.state.selectedOptions[item.companionFor] &&
        !this.state.selectedOptions[item.id]
    );
  }

  onInput(event) {
    /* Clear a quote field's inline error as soon as the user edits it. */
    const quoteField = event.target.closest('[data-quote-field]');
    if (quoteField && this.contains(quoteField)) {
      quoteField.removeAttribute('aria-invalid');
      const name = quoteField.dataset.quoteField;
      const err = this.querySelector(`[data-quote-error-for="${name}"]`);
      if (err) {
        err.textContent = '';
        err.hidden = true;
      }
      return;
    }

    const field = event.target.closest('[data-aircraft-field]');
    if (!field || !this.contains(field)) return;

    this.syncAircraftField(field);
  }

  /* Autofill and <select> changes can arrive as 'change' rather than 'input';
     handle both so required-field validation stays in sync. */
  onChange(event) {
    const field = event.target.closest ? event.target.closest('[data-aircraft-field]') : null;
    if (field && this.contains(field)) this.syncAircraftField(field);
  }

  /* Read one aircraft input into state (trimmed) and re-render. Trimming means
     whitespace-only text is stored as '' and counts as incomplete. */
  syncAircraftField(field) {
    this.state.aircraft[field.dataset.aircraftField] = field.value.trim();
    this.render();
  }

  /* Write current aircraft state back into the form inputs. Called once after
     a restore and after "Start over" — never on every render, so it cannot
     fight the user's typing. Empty/placeholder values reset selects to their
     '' option and clear text inputs. */
  syncAircraftInputsFromState() {
    for (const field of this.querySelectorAll('[data-aircraft-field]')) {
      const value = this.state.aircraft[field.dataset.aircraftField];
      field.value = typeof value === 'string' ? value : '';
    }
  }

  onSubmit(event) {
    event.preventDefault();
    const form = event.target.closest ? event.target.closest('[data-quote-form]') : null;
    if (form && this.contains(form)) this.onQuoteSubmit(form);
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.state.arrangeUnitId) {
      event.preventDefault();
      this.cancelArrange();
      return;
    }

    const unit = event.target.closest ? event.target.closest('[data-unit]') : null;
    if (!unit || !this.contains(unit)) return;
    const unitId = unit.getAttribute('data-unit');

    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.onUnitTap(unitId);
      return;
    }

    if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && this.state.arrangeUnitId === unitId) {
      event.preventDefault();
      const index = SLOTS.indexOf(this.slotOfUnit(unitId)) + (event.key === 'ArrowLeft' ? -1 : 1);
      if (index >= 0 && index < SLOTS.length) {
        this.moveUnit(unitId, SLOTS[index], { keepSelection: true });
      }
    }
  }

  onQuantityAction(button) {
    const id = button.dataset.displayId;
    const display = this.getDisplay(id);
    if (!display) return;

    const { maxPerDisplay, maxTotalDisplays } = this.config.constraints;
    const action = button.dataset.qtyAction;
    const current = this.state.quantities[id] || 0;
    let note = '';

    if (action === 'add' || action === 'increase') {
      if (current >= maxPerDisplay) {
        note = `Panel limit reached — up to ${maxPerDisplay} ${display.name} displays per panel.`;
      } else if (this.totalDisplayCount() >= maxTotalDisplays) {
        note = `Panel limit reached — up to ${maxTotalDisplays} displays total in this preliminary layout.`;
      } else {
        this.state.quantities[id] = current + 1;
      }
    } else if (action === 'decrease' && current > 0) {
      this.state.quantities[id] = current - 1;
    }

    this.setDisplayNote(note);
    this.render();
    this.restoreQuantityFocus(id, action);
  }

  /* After "Add to panel" the add button hides (and after removing the
     last unit the stepper hides) — keep keyboard focus somewhere useful. */
  restoreQuantityFocus(id, action) {
    const card = this.querySelector(`[data-display-card="${id}"]`);
    if (!card) return;

    const qty = this.state.quantities[id] || 0;
    if (qty > 0 && action === 'add') {
      const increase = card.querySelector('[data-qty-action="increase"]');
      if (increase) increase.focus();
    } else if (qty === 0 && action === 'decrease') {
      const add = card.querySelector('[data-add-button]');
      if (add) add.focus();
    }
  }

  setDisplayNote(message) {
    const note = this.querySelector('[data-display-note]');
    if (note) note.textContent = message;
  }

  goToStep(step) {
    if (step === this.state.step) return;

    this.state.step = step;
    this.state.arrangeUnitId = null;

    /* Short Step 4 (Engine Monitoring) doesn't leave enough content below its
       header for that header to reach the top of the scroll container, so the
       scroll clamps short. Give the host ONE real viewport of bottom padding
       while Step 4 is active — guaranteed enough length beneath it inside the
       scroll container (.page-wrapper on desktop, see scrollActiveStepToTop) —
       and clear it for every other step (their natural height already suffices).
       It persists while Step 4 is active so the container cannot clamp back
       short. */
    this.style.paddingBottom = step === 4 ? `${window.innerHeight}px` : '';

    this.render();
    this.announce(`Step ${step} of 5: ${this.stepTitle(step)}`);

    const heading = this.querySelector(
      `[data-step-panel="${step}"] .neal-g3x-builder__step-title`
    );
    /* Move focus to the new step's title for screen-reader/keyboard users
       WITHOUT the browser's default scroll-into-view. preventScroll stops the
       focus call itself from moving the viewport; the deterministic re-anchor
       below owns where the page lands. These are two separate concerns — do not
       remove preventScroll (it also guards a focus-driven scroll of its own). */
    if (heading && typeof heading.focus === 'function') {
      heading.focus({ preventScroll: true });
    }

    this.scrollActiveStepToTop(step);
  }

  /* Position the newly active step so its "Step X of 5" eyebrow + heading sit
     just below the sticky site header, near the top of the viewport — never the
     progress nav, panel preview, summary, step-nav buttons, or footer.

     Critically, this theme does NOT scroll the window on desktop: at >=990px the
     Horizon base stylesheet pins html/body to 100dvh with overflow:hidden and
     makes .page-wrapper the scroll container (overflow-y:auto). So window.scrollTo
     / window.scrollY are inert there and must not be used — we scroll the ACTUAL
     scroll container (found by walking up from the builder), and fall back to the
     window only when nothing above us scrolls (e.g. the <990px layout).

     Two animation frames, AFTER render() has shown the new step: the temporary
     one-viewport host padding applied in goToStep() (for the short Step 4) must
     enter the container's layout first, or the step header can't reach the top
     and the scroll clamps short. Frame 1 lets that padding land; frame 2 measures
     the step header's live viewport position and scrolls the container by the
     delta needed to bring it to (sticky-header offset + gap). behavior:'instant'
     overrides the container's CSS `scroll-behavior: smooth`, so the step opens in
     its final position with no animation. Both frames re-check the step is still
     active so rapid navigation can't let an older frame move the page. */
  scrollActiveStepToTop(step) {
    if (typeof window.requestAnimationFrame !== 'function') return;

    window.requestAnimationFrame(() => {
      if (!this.isConnected || this.state.step !== step) return;

      window.requestAnimationFrame(() => {
        if (!this.isConnected || this.state.step !== step) return;

        const target = this.querySelector(
          `[data-step-panel="${step}"] .neal-g3x-builder__step-head`
        );
        if (!target || typeof target.getBoundingClientRect !== 'function') return;

        /* How far (viewport-space) the header sits from where we want it — just
           under the sticky site header. Identical in whichever element scrolls. */
        const delta =
          target.getBoundingClientRect().top - this.stickyHeaderOffset() - STEP_SCROLL_GAP;

        const container = this.scrollContainer();
        if (container) {
          container.scrollTo({ top: Math.max(0, container.scrollTop + delta), behavior: 'instant' });
        } else {
          window.scrollTo({ top: Math.max(0, window.scrollY + delta), behavior: 'instant' });
        }
      });
    });
  }

  /* The element that actually scrolls the page. Horizon makes .page-wrapper the
     scroll container at >=990px (html/body are locked to 100dvh + overflow:hidden),
     so the window is inert there; below 990px the window scrolls and this returns
     null. Walk up from the builder and return the nearest ancestor that both
     allows overflow and currently has scrollable content. */
  scrollContainer() {
    let el = this.parentElement;
    while (el && el.nodeType === 1 && el !== document.body && el !== document.documentElement) {
      const overflowY = window.getComputedStyle(el).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  /* Bottom edge (px from the viewport top) of the site header currently covering
     the top of the screen, so a step title is never tucked under it. Scoped to
     the theme's own header wrappers — deliberately NOT a page-wide sticky/fixed
     sweep, which could measure unrelated widgets. Takes the greatest bottom among
     the header elements that are genuinely sticky/fixed AND pinned at the top
     right now, so it adapts to the desktop vs. mobile header rows with no
     hard-coded height, and returns 0 when nothing is sticking (e.g. a scroll-up
     header that is currently hidden). */
  stickyHeaderOffset() {
    let offset = 0;
    for (const selector of SITE_HEADER_SELECTORS) {
      let el = null;
      try {
        el = document.querySelector(selector);
      } catch (error) {
        continue;
      }
      if (!el || typeof el.getBoundingClientRect !== 'function') continue;

      const style = window.getComputedStyle(el);
      if (style.position !== 'sticky' && style.position !== 'fixed') continue;
      if (style.visibility === 'hidden' || style.display === 'none') continue;

      const rect = el.getBoundingClientRect();
      /* Only a row pinned to (or above) the very top actually obstructs. */
      if (rect.bottom <= 0 || rect.top > 8) continue;
      if (rect.bottom > offset) offset = rect.bottom;
    }
    return offset;
  }

  announce(message) {
    const region = this.querySelector('[data-live-region]');
    if (region) region.textContent = message;
  }

  /* ------------------------------------------------------------------
     Pointer drag — units only; element-scoped listeners with capture
     ------------------------------------------------------------------ */

  svgPoint(event) {
    const svg = this.querySelector('[data-panel-svg]');
    if (!svg || typeof svg.getScreenCTM !== 'function') return null;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    return new DOMPoint(event.clientX, event.clientY).matrixTransform(ctm.inverse());
  }

  onPointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return;

    const unit = event.target.closest ? event.target.closest('[data-unit]') : null;
    if (!unit || !this.contains(unit)) {
      /* Tap on an empty slot target while a unit is selected → move. */
      const slotTarget = event.target.closest ? event.target.closest('[data-slot-target]') : null;
      if (slotTarget && this.contains(slotTarget) && this.state.arrangeUnitId) {
        event.preventDefault();
        this.moveUnit(this.state.arrangeUnitId, slotTarget.dataset.slotTarget);
      }
      return;
    }

    const point = this.svgPoint(event);
    if (!point) return;

    event.preventDefault();
    if (typeof unit.focus === 'function') unit.focus();
    try {
      unit.setPointerCapture(event.pointerId);
    } catch (error) {
      /* Pointer capture is an enhancement — drag still works within the element. */
    }

    this.dragState = {
      unitId: unit.getAttribute('data-unit'),
      fromSlot: unit.getAttribute('data-slot'),
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      svgStart: point,
      el: unit,
      moved: false,
      candidateSlot: null,
    };
  }

  onPointerMove(event) {
    const drag = this.dragState;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const distance = Math.hypot(event.clientX - drag.startClientX, event.clientY - drag.startClientY);
    if (!drag.moved && distance < DRAG_THRESHOLD) return;

    if (!drag.moved) {
      drag.moved = true;
      const svg = this.querySelector('[data-panel-svg]');
      if (svg) svg.classList.add('is-arranging');
      drag.el.classList.add('is-dragging');
      if (drag.el.parentNode) drag.el.parentNode.append(drag.el); // raise above siblings
    }

    const point = this.svgPoint(event);
    if (!point) return;

    const scale = this.lastRow ? this.lastRow.scale : 1;
    const dx = (point.x - drag.svgStart.x) / scale;
    const dy = (point.y - drag.svgStart.y) / scale;
    drag.el.setAttribute('transform', `translate(${dx} ${dy})`);

    this.updateDropCandidate(point);
  }

  onPointerUp(event) {
    const drag = this.dragState;
    if (!drag || event.pointerId !== drag.pointerId) return;
    this.dragState = null;

    try {
      drag.el.releasePointerCapture(event.pointerId);
    } catch (error) {
      /* Already released. */
    }

    if (!drag.moved) {
      this.onUnitTap(drag.unitId);
      return;
    }

    const point = this.svgPoint(event);
    const slot = point ? this.dropSlotAt(point) : null;

    if (slot && slot !== this.slotOfUnit(drag.unitId)) {
      this.moveUnit(drag.unitId, slot);
    } else {
      /* Invalid or same-slot drop — re-render snaps the unit back. */
      this.render();
    }
  }

  onPointerCancel(event) {
    const drag = this.dragState;
    if (!drag || event.pointerId !== drag.pointerId) return;
    this.dragState = null;
    this.render();
  }

  dropSlotAt(point) {
    if (!this.lastRow) return null;
    if (point.y < 40 || point.y > 545 || point.x < 25 || point.x > 1175) return null;

    let best = null;
    let bestDistance = Infinity;
    for (const entry of this.lastRow.entries) {
      const distance = Math.abs(entry.cx - point.x);
      if (distance < bestDistance) {
        best = entry;
        bestDistance = distance;
      }
    }
    if (!best || bestDistance > best.width / 2 + 70) return null;
    return best.slot;
  }

  updateDropCandidate(point) {
    const drag = this.dragState;
    if (!drag) return;

    const slot = this.dropSlotAt(point);
    if (drag.candidateSlot === slot) return;
    drag.candidateSlot = slot;

    for (const el of this.querySelectorAll('.is-drop-candidate, .is-swap-candidate')) {
      el.classList.remove('is-drop-candidate', 'is-swap-candidate');
    }
    if (!slot || slot === drag.fromSlot) return;

    const emptyTarget = this.querySelector(`[data-slot-target="${slot}"]`);
    if (emptyTarget) {
      emptyTarget.classList.add('is-drop-candidate');
    } else {
      const occupied = this.querySelector(`[data-panel-displays] [data-slot="${slot}"]`);
      if (occupied) occupied.classList.add('is-swap-candidate');
    }
  }

  /* ------------------------------------------------------------------
     Render pipeline — safe to run any number of times
     ------------------------------------------------------------------ */

  render() {
    /* Steps 2–4 rebuild their DOM each render, so remember which
       selectable control was focused and restore it afterward. */
    const focusSelector = this.captureFocusSelector();

    this.reconcileLayout();
    this.renderStepPanels();
    this.renderProgress();
    this.renderCards();
    this.renderCore();
    this.renderOptions();
    this.renderEngine();
    this.renderSummary();
    this.renderReview();
    this.renderTotals();
    this.renderPanelPreview();
    this.renderArrangeUI();
    this.renderPrimaryActions();

    this.restoreFocusSelector(focusSelector);
    this.persist();
  }

  captureFocusSelector() {
    const el = document.activeElement;
    if (!el || !this.contains(el) || !el.getAttribute) return null;
    for (const attr of ['data-option-toggle', 'data-sensor-toggle', 'data-engine-mode']) {
      const value = el.getAttribute(attr);
      if (value) return `[${attr}="${value}"]`;
    }
    return null;
  }

  restoreFocusSelector(selector) {
    if (!selector) return;
    const el = this.querySelector(selector);
    if (el && typeof el.focus === 'function') el.focus();
  }

  renderStepPanels() {
    for (const panel of this.querySelectorAll('[data-step-panel]')) {
      panel.hidden = Number.parseInt(panel.dataset.stepPanel, 10) !== this.state.step;
    }
  }

  /* ------------------------------------------------------------------
     Step-1 required-field validation + primary-action state — the single
     source of truth for the Continue button, the summary CTA, and Step-1
     progress completion.
     ------------------------------------------------------------------ */

  /* A required aircraft field is satisfied only by a real, non-placeholder
     value. Selects use empty-string placeholders and text is stored trimmed,
     so a trimmed non-empty string covers both cases. */
  aircraftFieldFilled(key) {
    return String(this.state.aircraft[key] || '').trim() !== '';
  }

  aircraftComplete() {
    return REQUIRED_AIRCRAFT_KEYS.every((key) => this.aircraftFieldFilled(key));
  }

  /* Step 1 is ready to advance once at least one display is selected AND every
     required aircraft field is filled. */
  step1Ready() {
    return this.totalDisplayCount() > 0 && this.aircraftComplete();
  }

  /* Label, secondary hint, target step, and visual state for the Configuration
     Summary CTA, derived entirely from builder state.
     - `disabled` is true only when NO display is selected: the button is a hard
       gate (truly disabled — no scroll, no navigation) and shows the hint.
     - `pending` picks the yellow "pending" style (Steps 1–4) over the blue
       "active" style (Step 5).
     - `target` is the step the button scrolls/navigates to when enabled:
       Select Displays / Add Aircraft Profile → Step 1; "Next: <title>" → the
       next step; Review & Request Quote → Step 5. */
  summaryCtaState() {
    if (this.state.step >= 5) {
      return { label: 'Review & Request Quote', hint: '', target: 5, disabled: false, pending: false };
    }
    if (this.totalDisplayCount() === 0) {
      return {
        label: 'Select Displays',
        hint: 'Add a display to continue',
        target: 1,
        disabled: true,
        pending: true,
      };
    }
    if (!this.aircraftComplete()) {
      return { label: 'Add Aircraft Profile', hint: '', target: 1, disabled: false, pending: true };
    }
    return {
      label: `Next: ${this.stepTitle(this.state.step + 1)}`,
      hint: '',
      target: this.state.step + 1,
      disabled: false,
      pending: true,
    };
  }

  /* Sync both primary CTAs to builder state on every render. Native `disabled`
     on the <button>s blocks mouse, Enter/Space, and programmatic clicks, so no
     separate keyboard/pointer guard is needed. */
  renderPrimaryActions() {
    const cont = this.querySelector('[data-step1-continue]');
    if (cont) {
      const ready = this.step1Ready();
      cont.disabled = !ready;
      cont.classList.toggle('neal-g3x-builder__next--ready', ready);
    }

    const cta = this.querySelector('[data-summary-cta]');
    if (cta) {
      const { label, hint, target, disabled, pending } = this.summaryCtaState();
      const labelEl = cta.querySelector('[data-summary-cta-label]') || cta;
      labelEl.textContent = label;

      /* Secondary hint appears only in the disabled "no display selected" gate. */
      const hintEl = cta.querySelector('[data-summary-cta-hint]');
      if (hintEl) {
        hintEl.textContent = hint;
        hintEl.hidden = !hint;
      }

      /* Enabled → scrolls/navigates to `target` (handled in onClick). Disabled
         (no displays yet) → a hard gate: truly disabled, so a click can't scroll
         or navigate. */
      cta.dataset.stepTarget = String(target);
      cta.disabled = disabled;
      cta.classList.toggle('neal-g3x-builder__summary-quote--pending', pending);
    }
  }

  isStepComplete(step) {
    if (step === 1) {
      /* Combined "Select Displays & Aircraft Profile": complete once a display
         is selected AND every required aircraft field is filled. */
      return this.step1Ready();
    }
    return false; // Steps 2–5 become completable in a later phase.
  }

  renderProgress() {
    const items = this.querySelectorAll('.neal-g3x-builder__progress-item');
    items.forEach((item, index) => {
      const step = index + 1;
      const button = item.querySelector('[data-step-target]');
      const isActive = step === this.state.step;
      const isComplete = !isActive && this.isStepComplete(step);

      item.classList.toggle('is-active', isActive);
      item.classList.toggle('is-complete', isComplete);

      if (button) {
        if (isActive) {
          button.setAttribute('aria-current', 'step');
        } else {
          button.removeAttribute('aria-current');
        }

        let label = `Step ${step} of 5: ${this.stepTitle(step)}`;
        if (isActive) label += ' (current step)';
        if (isComplete) label += ' (completed)';
        button.setAttribute('aria-label', label);
      }
    });
  }

  renderCards() {
    for (const display of this.config.displays) {
      const card = this.querySelector(`[data-display-card="${display.id}"]`);
      if (!card) continue;

      const qty = this.state.quantities[display.id] || 0;
      card.classList.toggle('is-selected', qty > 0);

      const addButton = card.querySelector('[data-add-button]');
      const qtyControl = card.querySelector('[data-qty-control]');
      const qtyValue = card.querySelector('[data-qty-value]');

      if (addButton) addButton.hidden = qty > 0;
      if (qtyControl) qtyControl.hidden = qty === 0;
      if (qtyValue) qtyValue.textContent = String(qty);
    }
  }

  /* ------------------------------------------------------------------
     Small DOM helpers
     ------------------------------------------------------------------ */

  makeEl(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  makeBadge(variant, text) {
    return this.makeEl(
      'span',
      `neal-g3x-builder__badge neal-g3x-builder__badge--${variant}`,
      text
    );
  }

  /* ------------------------------------------------------------------
     Part-number links — every visible Garmin SKU opens Neal Avionics'
     exact-SKU Shopify product search in a new tab. Single source of truth
     for the URL and the link element (no per-call-site variants).
     ------------------------------------------------------------------ */

  /* Reusable relative search URL, e.g. 010-02319-00 →
     /search?type=product&q=%22010-02319-00%22 */
  productSearchUrl(partNumber) {
    const sku = String(partNumber || '').trim();
    if (!sku) return '';
    return `/search?type=product&q=${encodeURIComponent(`"${sku}"`)}`;
  }

  /* An <a> to the exact-SKU search, opened in a new tab. Built with DOM APIs, so
     the SKU text and attributes are escaped by the browser (no manual escaping
     needed). Returns null for an empty SKU. */
  partNumberLink(partNumber) {
    const sku = String(partNumber || '').trim();
    if (!sku) return null;
    const link = this.makeEl('a', 'neal-g3x-builder__part-link', sku);
    link.href = this.productSearchUrl(sku);
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.setAttribute('aria-label', `View product details for part number ${sku} in a new tab`);
    return link;
  }

  /* A <span>/<p> for a visible part number: an optional plain "P/N " label
     followed by the linked SKU (falls back to plain text if the SKU is empty). */
  partNumberEl(tag, className, partNumber, withPrefix) {
    const el = this.makeEl(tag, className);
    if (withPrefix) el.append(document.createTextNode('P/N '));
    el.append(this.partNumberLink(partNumber) || document.createTextNode(String(partNumber || '')));
    return el;
  }

  /* Append "<leading text>…P/N <sku-link>" to a parent node, for rows whose SKU
     is embedded in a longer sentence (summary / review display lines). */
  appendPartNumberText(parent, leadingText, partNumber) {
    parent.append(document.createTextNode(leadingText));
    parent.append(this.partNumberLink(partNumber) || document.createTextNode(String(partNumber || '')));
  }

  checkGlyph() {
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', 'm9.9 16.2-3.6-3.6 1.4-1.4 2.2 2.2 6-6 1.4 1.4-7.4 7.4Z');
    path.setAttribute('fill', 'currentColor');
    svg.append(path);
    return svg;
  }

  requiredKitCard(item, badgeVariant, badgeText, subline) {
    const card = this.makeEl('div', 'neal-g3x-builder__corekit');

    const head = this.makeEl('div', 'neal-g3x-builder__corekit-head');
    const names = this.makeEl('div', 'neal-g3x-builder__corekit-names');
    names.append(this.makeEl('h4', 'neal-g3x-builder__corekit-name', item.name));
    names.append(this.partNumberEl('p', 'neal-g3x-builder__corekit-part', item.partNumber, true));
    head.append(names);

    const badges = this.makeEl('div', 'neal-g3x-builder__corekit-badges');
    for (const badge of [].concat(badgeVariant)) {
      const idx = [].concat(badgeVariant).indexOf(badge);
      badges.append(this.makeBadge(badge, [].concat(badgeText)[idx]));
    }
    head.append(badges);
    card.append(head);

    if (subline) card.append(this.makeEl('p', 'neal-g3x-builder__opt-form', subline));
    return card;
  }

  /* ------------------------------------------------------------------
     Step 2 — Certified Core System (required, auto-included)
     ------------------------------------------------------------------ */

  renderCore() {
    const mount = this.querySelector('[data-core-mount]');
    if (!mount) return;
    mount.replaceChildren();

    const core = this.config.coreSystem;
    if (!core || !Array.isArray(core.kits)) return;

    mount.append(
      this.makeEl(
        'p',
        'neal-g3x-builder__step-note',
        'Required for every certified G3X Touch system and included automatically.'
      )
    );

    for (const kit of core.kits) {
      const card = this.requiredKitCard(kit, ['required', 'included'], ['Required', 'Auto-included']);

      if (Array.isArray(kit.components) && kit.components.length) {
        const details = this.makeEl('details', 'neal-g3x-builder__components');
        const summary = document.createElement('summary');
        summary.textContent = `Included components (${kit.components.length})`;
        details.append(summary);

        const list = this.makeEl('ul');
        for (const comp of kit.components) {
          const li = document.createElement('li');
          li.append(this.makeEl('span', 'neal-g3x-builder__comp-name', comp.name));
          li.append(this.partNumberEl('span', 'neal-g3x-builder__pn', comp.partNumber, false));
          list.append(li);
        }
        details.append(list);
        card.append(details);
      }

      mount.append(card);
    }
  }

  /* ------------------------------------------------------------------
     Step 3 — Interfaces & Options (selectable, grouped)
     ------------------------------------------------------------------ */

  renderOptions() {
    const mount = this.querySelector('[data-options-mount]');
    if (!mount) return;
    mount.replaceChildren();

    const groups = this.optionGroups();
    if (!groups.length) return;

    for (const group of groups) {
      const cat = this.makeEl('section', 'neal-g3x-builder__cat');
      cat.append(this.makeEl('h4', 'neal-g3x-builder__cat-title', group.title));

      const grid = this.makeEl('div', 'neal-g3x-builder__optgrid');
      for (const item of group.items || []) grid.append(this.buildSelectButton(item, 'option'));
      cat.append(grid);
      mount.append(cat);
    }

    mount.append(
      this.makeEl(
        'p',
        'neal-g3x-builder__step-note',
        'All options are optional and preliminary. Connector kits are recommended companions — Neal Avionics confirms compatibility and required parts before quoting.'
      )
    );
  }

  /* Shared selectable card for Step 3 options and Step 4 sensors. */
  buildSelectButton(item, kind) {
    const store = kind === 'sensor' ? this.state.selectedSensors : this.state.selectedOptions;
    const selected = Boolean(store[item.id]);
    const attr = kind === 'sensor' ? 'data-sensor-toggle' : 'data-option-toggle';

    const btn = this.makeEl('button', `neal-g3x-builder__opt${selected ? ' is-selected' : ''}`);
    btn.type = 'button';
    btn.setAttribute(attr, item.id);
    btn.setAttribute('aria-pressed', selected ? 'true' : 'false');

    const check = this.makeEl('span', 'neal-g3x-builder__opt-check');
    check.setAttribute('aria-hidden', 'true');
    check.append(this.checkGlyph());
    btn.append(check);

    const body = this.makeEl('span', 'neal-g3x-builder__opt-body');
    const head = this.makeEl('span', 'neal-g3x-builder__opt-head');
    head.append(this.makeEl('span', 'neal-g3x-builder__opt-name', item.name));
    head.append(this.partNumberEl('span', 'neal-g3x-builder__pn', item.partNumber, true));
    body.append(head);
    if (item.form) body.append(this.makeEl('span', 'neal-g3x-builder__opt-form', item.form));

    const badges = this.makeEl('span', 'neal-g3x-builder__opt-badges');
    if (item.companionFor) badges.append(this.makeBadge('companion', 'Companion'));
    if (selected) badges.append(this.makeBadge('selected', 'Selected'));
    if (badges.childNodes.length) body.append(badges);

    btn.append(body);
    return btn;
  }

  /* ------------------------------------------------------------------
     Step 4 — Engine Monitoring (responds to aircraft cylinder count)
     ------------------------------------------------------------------ */

  renderEngine() {
    const mount = this.querySelector('[data-engine-mount]');
    if (!mount) return;
    mount.replaceChildren();

    const engine = this.engineData();
    if (!engine) return;

    const modes = this.makeEl('div', 'neal-g3x-builder__engine-modes');
    modes.setAttribute('role', 'group');
    modes.setAttribute('aria-label', 'G3X engine monitoring');
    modes.append(
      this.buildEngineMode('none', 'No G3X engine monitoring', 'Keep your existing engine instruments.')
    );
    modes.append(
      this.buildEngineMode('add', 'Add G3X engine monitoring', 'Bring engine data onto your G3X Touch displays.')
    );
    mount.append(modes);

    if (this.state.engineMonitoring !== 'add') return;

    /* GEA 24B — included with engine monitoring */
    if (engine.interface) {
      const section = this.makeEl('div', 'neal-g3x-builder__engine-section');
      section.append(this.makeEl('h4', 'neal-g3x-builder__cat-title', 'Included with engine monitoring'));
      const card = this.requiredKitCard(engine.interface, 'included', 'Included', engine.interface.form);
      section.append(card);
      mount.append(section);
    }

    /* Sensor-kit recommendation from cylinder count — never guessed */
    const kitSection = this.makeEl('div', 'neal-g3x-builder__engine-section');
    kitSection.append(this.makeEl('h4', 'neal-g3x-builder__cat-title', 'Recommended engine sensor kit'));
    const kit = this.recommendedSensorKit();
    if (kit) {
      kitSection.append(
        this.requiredKitCard(
          kit,
          'recommended',
          'Recommended',
          `Based on your ${this.cylinderCount()}-cylinder aircraft — subject to Neal Avionics verification.`
        )
      );
    } else {
      const needinfo = this.makeEl('div', 'neal-g3x-builder__needinfo');
      needinfo.append(
        this.makeEl(
          'p',
          null,
          "Aircraft details required — add your engine cylinder count in Step 1 so we can recommend the correct sensor kit. We won't guess."
        )
      );
      const back = this.makeEl('button', 'neal-g3x-builder__back', 'Return to Step 1');
      back.type = 'button';
      back.setAttribute('data-step-target', '1');
      needinfo.append(back);
      kitSection.append(needinfo);
    }
    mount.append(kitSection);

    /* Additional sensors */
    const sensors = this.engineSensors();
    if (sensors.length) {
      const section = this.makeEl('div', 'neal-g3x-builder__engine-section');
      section.append(this.makeEl('h4', 'neal-g3x-builder__cat-title', 'Additional sensors (optional)'));
      const grid = this.makeEl('div', 'neal-g3x-builder__optgrid');
      for (const sensor of sensors) grid.append(this.buildSelectButton(sensor, 'sensor'));
      section.append(grid);
      section.append(
        this.makeEl(
          'p',
          'neal-g3x-builder__step-note',
          'Sensor selections are preliminary. Exact sensors and quantities are determined by Neal Avionics for your engine.'
        )
      );
      mount.append(section);
    }
  }

  buildEngineMode(mode, title, sub) {
    const active = this.state.engineMonitoring === mode;
    const btn = this.makeEl('button', `neal-g3x-builder__engine-mode${active ? ' is-active' : ''}`);
    btn.type = 'button';
    btn.setAttribute('data-engine-mode', mode);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.append(this.makeEl('span', 'neal-g3x-builder__engine-mode-title', title));
    btn.append(this.makeEl('span', 'neal-g3x-builder__engine-mode-sub', sub));
    return btn;
  }

  aircraftSummaryLine() {
    const { aircraft } = this.state;
    const identity = [aircraft.year, aircraft.make, aircraft.model]
      .filter(Boolean)
      .join(' ');
    const details = [aircraft.engines, aircraft.cylinders].filter(Boolean);

    return [identity, ...details].filter(Boolean).join(' · ');
  }

  selectedDisplays() {
    return PANEL_ORDER.map((id) => {
      const display = this.getDisplay(id);
      const qty = this.state.quantities[id] || 0;
      return display && qty > 0 ? { display, qty } : null;
    }).filter(Boolean);
  }

  /* ------------------------------------------------------------------
     Pricing — public Garmin LIST prices only (integer cents). Never dealer
     cost or MAP. The total covers selected equipment plus the auto-included
     certified core; installation, freight, tax, and any parts Neal Avionics
     later determines are required are excluded.
     ------------------------------------------------------------------ */

  /* List price for a part number, in integer cents (0 if absent — e.g. kit
     sub-components whose cost is bundled into the parent kit). */
  listPriceCents(partNumber) {
    return G3X_LIST_PRICES_CENTS[partNumber] || 0;
  }

  /* Priced line items: selected displays (× quantity), the auto-included core
     kits (only once a display makes it a system), selected options, and — when
     engine monitoring is added — the auto-included GEA 24B, the cylinder-
     appropriate engine sensor kit (a required part of the config, priced as the
     kit; its bundled child sensors are never totaled separately), and any
     selected additional sensors. Still excludes recommended companion connector
     kits (advisory — only priced when the customer actually selects them). */
  pricingLines() {
    const lines = [];
    const push = (partNumber, name, quantity, required) => {
      const unit = this.listPriceCents(partNumber);
      lines.push({
        partNumber,
        name,
        quantity,
        listPriceCents: unit,
        lineTotalCents: unit * quantity,
        required: Boolean(required),
      });
    };

    const displays = this.selectedDisplays();
    for (const { display, qty } of displays) push(display.partNumber, display.name, qty, false);

    if (displays.length > 0) {
      const core = this.config.coreSystem;
      if (core && Array.isArray(core.kits)) {
        for (const kit of core.kits) push(kit.partNumber, kit.name, 1, true);
      }
    }

    for (const opt of this.selectedOptionList()) push(opt.partNumber, opt.name, 1, false);

    if (this.state.engineMonitoring === 'add') {
      const engine = this.engineData();
      if (engine && engine.interface) push(engine.interface.partNumber, engine.interface.name, 1, true);
      /* Cylinder-appropriate engine sensor kit — a required part of the config,
         not advisory. Priced as the kit only; its bundled child sensors are never
         totaled separately. Uses the authoritative Aircraft Profile cylinder-count
         state via recommendedSensorKit() (4 → K00-01010-11, 6 → K00-01011-11);
         null (missing/unsupported count) adds NO price and is surfaced by
         pricingNeedsSensorKitConfirmation(). */
      const sensorKit = this.recommendedSensorKit();
      if (sensorKit) push(sensorKit.partNumber, sensorKit.name, 1, true);
      for (const sensor of this.selectedSensorList()) push(sensor.partNumber, sensor.name, 1, false);
    }

    return lines;
  }

  /* Sum of the priced lines, in integer cents (matches what the payload sends). */
  computeListTotalCents() {
    return this.pricingLines().reduce((sum, line) => sum + line.lineTotalCents, 0);
  }

  /* True when engine monitoring is selected but the Aircraft Profile cylinder
     count is missing/unsupported, so the sensor kit could not be priced — the
     total is incomplete and needs cylinder-count confirmation (no price invented). */
  pricingNeedsSensorKitConfirmation() {
    return this.state.engineMonitoring === 'add' && !this.recommendedSensorKit();
  }

  /* Integer cents → "$1,234.56" with no floating-point dollar math. */
  formatListPrice(cents) {
    const safe = Math.max(0, Math.round(cents));
    const dollars = Math.floor(safe / 100).toLocaleString('en-US');
    const remainder = String(safe % 100).padStart(2, '0');
    return `$${dollars}.${remainder}`;
  }

  /* Update every list-price total slot (summary rail + review step). */
  renderTotals() {
    const text = this.formatListPrice(this.computeListTotalCents());
    for (const el of this.querySelectorAll('[data-list-total]')) el.textContent = text;

    /* Warn when the engine sensor kit could not be priced (monitoring on, no valid
       cylinder count). No price is invented — the customer is asked to confirm. */
    const needsConfirm = this.pricingNeedsSensorKitConfirmation();
    for (const el of this.querySelectorAll('[data-list-total-warning]')) el.hidden = !needsConfirm;
  }

  renderSummary() {
    const stepChip = this.querySelector('[data-summary-step]');
    if (stepChip) {
      stepChip.textContent = `Step ${this.state.step} of 5 · ${this.stepTitle(this.state.step)}`;
    }

    const aircraftSlot = this.querySelector('[data-summary-aircraft]');
    if (aircraftSlot) {
      aircraftSlot.textContent = this.aircraftSummaryLine() || 'Not started';
    }

    const displaysSlot = this.querySelector('[data-summary-displays]');
    if (displaysSlot) {
      const selected = this.selectedDisplays();
      displaysSlot.replaceChildren();

      if (selected.length === 0) {
        displaysSlot.textContent = 'None selected';
      } else {
        const list = document.createElement('ul');
        for (const { display, qty } of selected) {
          const item = document.createElement('li');
          this.appendPartNumberText(item, `${qty} × ${display.summaryLabel} — P/N `, display.partNumber);
          list.append(item);
        }
        displaysSlot.append(list);
      }
    }

    /* Visual-only arrangement line — informational, never quote data. */
    const arrangementRow = this.querySelector('[data-summary-arrangement-row]');
    const arrangementSlot = this.querySelector('[data-summary-arrangement]');
    if (arrangementRow && arrangementSlot) {
      const parts = SLOTS.filter((slot) => this.state.layout[slot]).map(
        (slot) => `${capitalize(slot)}: ${this.unitLabel(this.state.layout[slot])}`
      );
      arrangementRow.hidden = parts.length === 0;
      arrangementSlot.textContent = parts.join(' · ');
    }

    const coreSlot = this.querySelector('[data-summary-core]');
    if (coreSlot) {
      const kits = (this.config.coreSystem && this.config.coreSystem.kits) || [];
      coreSlot.textContent = kits.length ? `Included — ${kits.length} required kits` : 'Included (required)';
      coreSlot.classList.remove('neal-g3x-builder__summary-pending');
    }

    const optionsSlot = this.querySelector('[data-summary-options]');
    if (optionsSlot) {
      const count = this.selectedOptionList().length;
      optionsSlot.textContent = count ? `${count} selected` : 'None selected';
      optionsSlot.classList.toggle('neal-g3x-builder__summary-pending', count === 0);
    }

    const engineSlot = this.querySelector('[data-summary-engine]');
    if (engineSlot) {
      if (this.state.engineMonitoring === 'add') {
        const kit = this.recommendedSensorKit();
        const extra = this.selectedSensorList().length;
        let text = 'Added — GEA 24B';
        if (kit) text += `, ${kit.name}`;
        if (extra) text += `, ${extra} sensor${extra === 1 ? '' : 's'}`;
        engineSlot.textContent = text;
        engineSlot.classList.remove('neal-g3x-builder__summary-pending');
      } else {
        engineSlot.textContent = 'Not added';
        engineSlot.classList.add('neal-g3x-builder__summary-pending');
      }
    }
  }

  renderReview() {
    const recap = this.querySelector('[data-review-recap]');
    if (!recap) return;

    recap.replaceChildren();

    /* Aircraft profile */
    const aircraftItems = [];
    const identity = this.aircraftSummaryLine();
    if (identity) aircraftItems.push(identity);
    for (const [key, label] of Object.entries(AIRCRAFT_EQUIPMENT_LABELS)) {
      if (this.state.aircraft[key]) aircraftItems.push(`${label}: ${this.state.aircraft[key]}`);
    }
    recap.append(this.reviewRow('Aircraft profile', aircraftItems.length ? aircraftItems : ['Not provided yet']));

    /* Displays + visual arrangement (arrangement is layout only) */
    const selected = this.selectedDisplays();
    const displayLines = selected.length
      ? selected.map(({ display, qty }) => {
          /* Fragment so the embedded SKU can be a link; reviewRow renders Nodes. */
          const frag = document.createDocumentFragment();
          this.appendPartNumberText(frag, `${qty} × ${display.name} (${display.form}) — P/N `, display.partNumber);
          return frag;
        })
      : ['None selected'];
    const arrangement = SLOTS.filter((slot) => this.state.layout[slot]).map(
      (slot) => `${capitalize(slot)}: ${this.unitLabel(this.state.layout[slot])}`
    );
    if (arrangement.length) displayLines.push(`Visual arrangement — ${arrangement.join(' · ')} (layout only)`);
    recap.append(this.reviewRow('Displays', displayLines));

    const engine = this.engineData();
    const monitoringAdded = this.state.engineMonitoring === 'add';

    /* Required equipment — certified core, plus GEA 24B when monitoring is on */
    const requiredLines = [];
    const core = this.config.coreSystem;
    if (core && Array.isArray(core.kits)) {
      for (const kit of core.kits) requiredLines.push({ name: kit.name, pn: kit.partNumber });
    }
    if (monitoringAdded && engine && engine.interface) {
      requiredLines.push({ name: `${engine.interface.name} (with engine monitoring)`, pn: engine.interface.partNumber });
    }
    recap.append(this.reviewCategory('required', 'Required equipment', requiredLines));

    /* Customer-selected options */
    const selectedLines = [
      { name: monitoringAdded ? 'Engine monitoring: Added' : 'Engine monitoring: Not added' },
    ];
    for (const opt of this.selectedOptionList()) selectedLines.push({ name: opt.name, pn: opt.partNumber });
    if (monitoringAdded) {
      for (const sensor of this.selectedSensorList()) selectedLines.push({ name: sensor.name, pn: sensor.partNumber });
    }
    if (selectedLines.length === 1) selectedLines.push({ name: 'No optional equipment selected' });
    recap.append(this.reviewCategory('selected', 'Customer-selected options', selectedLines));

    /* Recommended equipment — sensor kit + companion connector kits */
    const recommendedLines = [];
    if (monitoringAdded) {
      const kit = this.recommendedSensorKit();
      if (kit) recommendedLines.push({ name: `${kit.name} (from cylinder count)`, pn: kit.partNumber });
    }
    for (const comp of this.recommendedCompanions()) {
      recommendedLines.push({ name: `${comp.name} (companion for a selected item)`, pn: comp.partNumber });
    }
    if (recommendedLines.length) {
      recap.append(this.reviewCategory('recommended', 'Recommended equipment', recommendedLines));
    }

    /* Verification required */
    recap.append(
      this.reviewCategory(
        'verify',
        'Verification required',
        this.verificationWarnings().map((name) => ({ name }))
      )
    );
  }

  /* Shared by the review category and the submitted payload. */
  verificationWarnings() {
    const out = [
      'Final configuration, compatibility, and required parts are verified by Neal Avionics before quoting.',
    ];
    const monitoringAdded = this.state.engineMonitoring === 'add';
    if (monitoringAdded && !this.recommendedSensorKit()) {
      out.push('Engine cylinder count needed (Step 1) to finalize the sensor kit.');
    }
    if (monitoringAdded && this.selectedSensorList().length) {
      out.push('Sensor selections are preliminary — exact sensors and quantities are set by Neal Avionics.');
    }
    if (this.recommendedCompanions().length) {
      out.push('Recommended companion connector kits are suggestions, not enforced requirements.');
    }
    return out;
  }

  reviewCategory(variant, title, items) {
    const section = this.makeEl('div', `neal-g3x-builder__review-cat neal-g3x-builder__review-cat--${variant}`);
    section.append(this.makeEl('p', 'neal-g3x-builder__review-cat-title', title));

    const list = this.makeEl(
      'ul',
      `neal-g3x-builder__review-list${variant === 'verify' ? ' neal-g3x-builder__verify' : ''}`
    );
    for (const item of items) {
      const li = document.createElement('li');
      li.append(this.makeEl('span', 'neal-g3x-builder__review-item-name', item.name));
      if (item.pn) li.append(this.partNumberEl('span', 'neal-g3x-builder__pn', item.pn, true));
      list.append(li);
    }
    section.append(list);
    return section;
  }

  reviewRow(term, lines) {
    const row = document.createElement('div');
    row.className = 'neal-g3x-builder__review-row';

    const dt = document.createElement('dt');
    dt.textContent = term;
    row.append(dt);

    const dd = document.createElement('dd');
    if (lines.length === 1) {
      const only = lines[0];
      if (only instanceof Node) dd.append(only);
      else dd.textContent = only;
    } else {
      const list = document.createElement('ul');
      for (const line of lines) {
        const item = document.createElement('li');
        if (line instanceof Node) item.append(line);
        else item.textContent = line;
        list.append(item);
      }
      dd.append(list);
    }
    row.append(dd);

    return row;
  }

  /* ------------------------------------------------------------------
     Quote submission (Step 5) — structured payload, no pricing
     ------------------------------------------------------------------ */

  stampQuoteFormStart() {
    const started = this.querySelector('[data-quote-started]');
    if (started && !started.value) started.value = String(Date.now());
  }

  readQuoteFields(form) {
    const get = (name) => {
      const el = form.querySelector(`[data-quote-field="${name}"]`);
      return el ? el.value.trim() : '';
    };
    return {
      name: get('name'),
      email: get('email'),
      phone: get('phone'),
      nNumber: get('nNumber').toUpperCase().replace(/[\s-]+/g, ''),
      preferredContact: get('preferredContact'),
      installLocation: get('installLocation'),
      notes: get('notes'),
    };
  }

  validateQuote(fields) {
    const errors = {};
    if (!fields.name) errors.name = 'Please enter your name.';
    if (!fields.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) {
      errors.email = 'Please enter a valid email address.';
    }
    if (!fields.phone || fields.phone.replace(/\D/g, '').length < 7) {
      errors.phone = 'Please enter a valid phone number.';
    }
    if (!/^N[0-9A-Z]{1,5}$/.test(fields.nNumber)) {
      errors.nNumber = 'Please enter a valid N-number, e.g. N620G.';
    }
    return errors;
  }

  applyQuoteErrors(form, errors) {
    for (const name of ['name', 'email', 'phone', 'nNumber']) {
      const el = form.querySelector(`[data-quote-field="${name}"]`);
      const err = form.querySelector(`[data-quote-error-for="${name}"]`);
      const has = Boolean(errors[name]);
      if (el) {
        if (has) el.setAttribute('aria-invalid', 'true');
        else el.removeAttribute('aria-invalid');
      }
      if (err) {
        err.textContent = errors[name] || '';
        err.hidden = !has;
      }
    }
  }

  setQuoteSubmitting(form, busy) {
    const button = form.querySelector('[data-quote-submit]');
    const spinner = form.querySelector('[data-quote-spinner]');
    const label = form.querySelector('[data-quote-submit-label]');
    if (button) {
      button.disabled = busy;
      button.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
    if (spinner) spinner.hidden = !busy;
    if (label) label.textContent = busy ? 'Sending…' : 'Verify My G3X Quote';
  }

  showQuoteError(form, message) {
    const box = form.querySelector('[data-quote-error]');
    if (box) {
      box.textContent = message;
      box.hidden = false;
    }
  }

  hideQuoteError(form) {
    const box = form.querySelector('[data-quote-error]');
    if (box) {
      box.textContent = '';
      box.hidden = true;
    }
  }

  showQuoteSuccess(result) {
    const form = this.querySelector('[data-quote-form]');
    const success = this.querySelector('[data-quote-success]');
    if (form) form.hidden = true;
    if (!success) return;

    success.hidden = false;

    const msg = success.querySelector('[data-quote-success-msg]');
    if (msg && result && result.message) msg.textContent = result.message;

    const ref = success.querySelector('[data-quote-ref]');
    if (ref) {
      if (result && result.reference) {
        ref.textContent = `Reference: ${result.reference}`;
        ref.hidden = false;
      } else {
        ref.hidden = true;
      }
    }

    success.setAttribute('tabindex', '-1');
    if (typeof success.focus === 'function') success.focus();
  }

  resetQuoteUi() {
    const form = this.querySelector('[data-quote-form]');
    const success = this.querySelector('[data-quote-success]');
    if (form) {
      form.hidden = false;
      if (typeof form.reset === 'function') form.reset();
      for (const el of form.querySelectorAll('[aria-invalid="true"]')) el.removeAttribute('aria-invalid');
      for (const el of form.querySelectorAll('[data-quote-error-for]')) {
        el.textContent = '';
        el.hidden = true;
      }
      this.hideQuoteError(form);
      const started = form.querySelector('[data-quote-started]');
      if (started) started.value = String(Date.now());
    }
    if (success) success.hidden = true;
  }

  onQuoteSubmit(form) {
    if (this.submitting) return; // prevent duplicate submissions in flight

    const fields = this.readQuoteFields(form);
    const errors = this.validateQuote(fields);
    this.applyQuoteErrors(form, errors);

    if (Object.keys(errors).length) {
      const firstInvalid = form.querySelector('[aria-invalid="true"]');
      if (firstInvalid && typeof firstInvalid.focus === 'function') firstInvalid.focus();
      this.showQuoteError(form, 'Please correct the highlighted fields and try again.');
      return;
    }
    this.hideQuoteError(form);

    /* Normalize the N-number back into the field for the customer. */
    const nEl = form.querySelector('[data-quote-field="nNumber"]');
    if (nEl) nEl.value = fields.nNumber;

    /* Honeypot: a filled hidden field means a bot — quietly show success
       without sending anything. */
    const honeypot = form.querySelector('[name="company_website"]');
    if (honeypot && honeypot.value.trim() !== '') {
      this.showQuoteSuccess({});
      return;
    }

    const endpoint = form.dataset.quoteEndpoint;
    if (!endpoint) {
      this.showQuoteError(form, 'Submission is not configured yet. Please call (619) 663-1000.');
      return;
    }

    this.submitting = true;
    this.setQuoteSubmitting(form, true);

    const startedEl = form.querySelector('[data-quote-started]');
    const body = new FormData();
    body.append('payload', JSON.stringify(this.buildQuotePayload(fields)));
    body.append('company_website', honeypot ? honeypot.value : '');
    body.append('form_started_at', startedEl ? startedEl.value : '');
    body.append('source', 'g3x-panel-builder');

    fetch(endpoint, { method: 'POST', body, mode: 'cors', credentials: 'omit' })
      .then((response) =>
        response
          .json()
          .catch(() => {
            throw new Error('The server returned an unexpected response.');
          })
          .then((result) => {
            if (!response.ok || !result || !result.success) {
              throw new Error((result && result.message) || 'We could not send your request. Please try again.');
            }
            return result;
          })
      )
      .then((result) => {
        this.showQuoteSuccess(result);
        this.announce('Your G3X quote request was sent. Neal Avionics will verify compatibility and contact you.');
      })
      .catch((error) => {
        this.showQuoteError(
          form,
          error && error.message
            ? error.message
            : 'We could not send your request. Please try again, or call (619) 663-1000.'
        );
        this.announce('Your request could not be sent.');
      })
      .then(() => {
        this.submitting = false;
        this.setQuoteSubmitting(form, false);
      });
  }

  layoutLabel(slot) {
    const unitId = this.state.layout[slot];
    return unitId ? this.unitLabel(unitId) : null;
  }

  corePayload() {
    const core = this.config.coreSystem;
    if (!core || !Array.isArray(core.kits)) return null;
    return {
      required: true,
      kits: core.kits.map((kit) => ({
        name: kit.name,
        partNumber: kit.partNumber,
        components: (kit.components || []).map((comp) => ({ name: comp.name, partNumber: comp.partNumber })),
      })),
    };
  }

  buildQuotePayload(contact) {
    const s = this.state;
    const engine = this.engineData();
    const monitoringAdded = s.engineMonitoring === 'add';
    const kit = monitoringAdded ? this.recommendedSensorKit() : null;

    return {
      builderVersion: this.config.version || STORAGE_VERSION,
      submittedAt: new Date().toISOString(),
      source: 'g3x-panel-builder',
      scope: this.config.scope || 'certificated',
      contact: {
        name: contact.name,
        email: contact.email,
        phone: contact.phone,
        nNumber: contact.nNumber,
        preferredContact: contact.preferredContact,
        installLocation: contact.installLocation,
        notes: contact.notes,
      },
      aircraft: { ...s.aircraft },
      displays: this.selectedDisplays().map(({ display, qty }) => ({
        id: display.id,
        name: display.name,
        partNumber: display.partNumber,
        quantity: qty,
      })),
      visualArrangement: {
        note: 'Visual layout only — does not affect equipment quantities.',
        left: this.layoutLabel('left'),
        center: this.layoutLabel('center'),
        right: this.layoutLabel('right'),
      },
      coreSystem: this.corePayload(),
      options: this.selectedOptionList().map((opt) => ({
        id: opt.id,
        name: opt.name,
        partNumber: opt.partNumber,
      })),
      engineMonitoring: {
        added: monitoringAdded,
        interface:
          monitoringAdded && engine && engine.interface
            ? { name: engine.interface.name, partNumber: engine.interface.partNumber }
            : null,
        cylinderCount: this.cylinderCount(),
        recommendedSensorKit: kit ? { name: kit.name, partNumber: kit.partNumber } : null,
        additionalSensors: monitoringAdded
          ? this.selectedSensorList().map((sensor) => ({
              id: sensor.id,
              name: sensor.name,
              partNumber: sensor.partNumber,
            }))
          : [],
      },
      recommendedCompanions: this.recommendedCompanions().map((comp) => ({
        id: comp.id,
        name: comp.name,
        partNumber: comp.partNumber,
        companionFor: comp.companionFor,
      })),
      verificationWarnings: this.verificationWarnings(),
      /* Public Garmin LIST pricing for selected equipment only (integer cents +
         formatted). No dealer cost, MAP, labor, freight, or tax — those are never
         computed here. Preliminary; verified by Neal Avionics before quoting. */
      pricing: {
        currency: 'USD',
        basis: 'garmin-list',
        note: 'Garmin list prices for selected equipment only. Excludes installation, freight, tax, and any additional parts Neal Avionics determines are required. Preliminary — verified before quoting.',
        listTotalCents: this.computeListTotalCents(),
        listTotal: this.formatListPrice(this.computeListTotalCents()),
        sensorKitConfirmationRequired: this.pricingNeedsSensorKitConfirmation(),
        lines: this.pricingLines(),
      },
    };
  }

  /* ------------------------------------------------------------------
     SVG panel preview — slot-based positioning
     ------------------------------------------------------------------ */

  /* Compute the three-slot row. Occupied slots use the real unit width
     (plus recess); empty slots reserve a nominal width so positions
     stay meaningful and nothing shifts when arrangement mode starts. */
  slotRow() {
    const entries = SLOTS.map((slot) => {
      const unitId = this.state.layout[slot];
      const art = unitId ? DISPLAY_ART[this.unitType(unitId)] : null;
      /* The finished PNG has no recess frame, so a unit occupies exactly its
         own image width; empty slots reserve a nominal width. */
      const width = art ? art.width : EMPTY_SLOT_WIDTH;
      return { slot, unitId, art, width, cx: 0 };
    });

    const total =
      entries.reduce((sum, entry) => sum + entry.width, 0) + PANEL.gap * (entries.length - 1);
    const scale = Math.min(1, PANEL.maxRowWidth / total);

    let x = PANEL.centerX - total / 2;
    for (const entry of entries) {
      entry.cx = x + entry.width / 2;
      x += entry.width + PANEL.gap;
    }

    return { entries, scale, total };
  }

  buildUnit(entry) {
    const { unitId, art, slot } = entry;
    const x = entry.cx - art.width / 2;
    const y = PANEL.rowCenterY - art.height / 2;
    const selected = this.state.arrangeUnitId === unitId;

    const group = document.createElementNS(SVG_NS, 'g');
    group.classList.add('neal-g3x-builder__unit');
    if (selected) group.classList.add('is-arrange-selected');
    group.setAttribute('data-unit', unitId);
    group.setAttribute('data-slot', slot);
    group.setAttribute('tabindex', '0');
    group.setAttribute('role', 'button');
    group.setAttribute(
      'aria-label',
      `${this.unitLabel(unitId)}, ${slot} position.` +
        (selected
          ? ' Selected for arranging — use the arrow keys or position buttons; Escape cancels.'
          : ' Press Enter to arrange this display.')
    );

    /* The finished display PNG IS the complete visible unit — no recess,
       frame, border, background, or drop shadow is drawn around it. The
       transparent <g> above is only the positioning/drag wrapper.
       preserveAspectRatio keeps the artwork from stretching or cropping. */
    const image = document.createElementNS(SVG_NS, 'image');
    image.setAttribute('href', art.image);
    image.setAttributeNS(XLINK_NS, 'xlink:href', art.image);
    image.setAttribute('x', String(x));
    image.setAttribute('y', String(y));
    image.setAttribute('width', String(art.width));
    image.setAttribute('height', String(art.height));
    image.setAttribute('preserveAspectRatio', 'xMidYMid meet');

    /* Swap affordance shown only in arrangement mode via CSS. */
    const swapHint = document.createElementNS(SVG_NS, 'g');
    swapHint.classList.add('neal-g3x-builder__swap-hint');

    const swapRect = document.createElementNS(SVG_NS, 'rect');
    swapRect.setAttribute('x', String(x - SWAP_INSET_X - 5));
    swapRect.setAttribute('y', String(y - SWAP_INSET_Y - 5));
    swapRect.setAttribute('width', String(art.width + (SWAP_INSET_X + 5) * 2));
    swapRect.setAttribute('height', String(art.height + (SWAP_INSET_Y + 5) * 2));
    swapRect.setAttribute('rx', '19');

    const badge = document.createElementNS(SVG_NS, 'g');
    badge.classList.add('neal-g3x-builder__swap-glyph');
    badge.setAttribute(
      'transform',
      `translate(${x + art.width + SWAP_INSET_X - 6} ${y - SWAP_INSET_Y + 6})`
    );
    const badgeCircle = document.createElementNS(SVG_NS, 'circle');
    badgeCircle.setAttribute('r', '11');
    const badgeArrows = document.createElementNS(SVG_NS, 'path');
    badgeArrows.setAttribute(
      'd',
      'M-4.5 -2h6.5l-2.2-2.2M2 -2 -0.2 0.2M4.5 2h-6.5l2.2 2.2M-2 2l2.2-2.2'
    );
    badge.append(badgeCircle, badgeArrows);
    swapHint.append(swapRect, badge);

    group.append(image, swapHint);
    return group;
  }

  buildSlotTarget(entry) {
    const group = document.createElementNS(SVG_NS, 'g');
    group.classList.add('neal-g3x-builder__slot-target');
    group.setAttribute('data-slot-target', entry.slot);

    const width = EMPTY_SLOT_WIDTH - 10;
    const height = 210;
    const rect = document.createElementNS(SVG_NS, 'rect');
    rect.setAttribute('x', String(entry.cx - width / 2));
    rect.setAttribute('y', String(PANEL.rowCenterY - height / 2));
    rect.setAttribute('width', String(width));
    rect.setAttribute('height', String(height));
    rect.setAttribute('rx', '10');

    const label = document.createElementNS(SVG_NS, 'text');
    label.setAttribute('x', String(entry.cx));
    label.setAttribute('y', String(PANEL.rowCenterY + 4));
    label.setAttribute('text-anchor', 'middle');
    label.textContent = entry.slot.toUpperCase();

    group.append(rect, label);
    return group;
  }

  renderPanelPreview() {
    const svg = this.querySelector('[data-panel-svg]');
    const slotLayer = this.querySelector('[data-panel-slots]');
    const target = this.querySelector('[data-panel-displays]');
    const emptyState = this.querySelector('[data-panel-empty]');
    const caption = this.querySelector('[data-panel-caption]');
    if (!svg || !target || !slotLayer) return;

    const activeElement = document.activeElement;
    const focusedUnitId =
      activeElement && this.contains(activeElement) && activeElement.getAttribute
        ? activeElement.getAttribute('data-unit')
        : null;

    const units = this.desiredUnits();
    target.replaceChildren();
    slotLayer.replaceChildren();

    if (emptyState) {
      emptyState.style.display = units.length > 0 ? 'none' : '';
    }
    svg.classList.toggle('is-arranging', Boolean(this.state.arrangeUnitId));

    const row = this.slotRow();
    this.lastRow = row;

    if (units.length > 0) {
      const transform =
        row.scale < 1
          ? `translate(${PANEL.centerX * (1 - row.scale)} ${PANEL.rowCenterY * (1 - row.scale)}) scale(${row.scale})`
          : null;
      for (const layer of [slotLayer, target]) {
        if (transform) layer.setAttribute('transform', transform);
        else layer.removeAttribute('transform');
      }

      for (const entry of row.entries) {
        if (entry.unitId) target.append(this.buildUnit(entry));
        else slotLayer.append(this.buildSlotTarget(entry));
      }
    }

    const selected = this.selectedDisplays();
    if (caption) {
      caption.textContent = selected.length
        ? `${selected
            .map(({ display, qty }) => `${qty} × ${display.summaryLabel}`)
            .join('  +  ')} — system-layout preview, not to installation scale.`
        : 'System-layout preview — not to installation scale.';
    }

    let described = selected.length
      ? selected
          .map(({ display, qty }) => `${qty} ${display.name} ${qty === 1 ? 'display' : 'displays'}`)
          .join(' and ')
      : 'no displays selected yet';
    const order = row.entries
      .filter((entry) => entry.unitId)
      .map((entry) => this.unitLabel(entry.unitId));
    if (order.length > 0) {
      described += `. Left to right: ${order.join(', ')}`;
    }
    svg.setAttribute('aria-label', `Aircraft panel preview: ${described}`);

    if (focusedUnitId) {
      const refocus = this.querySelector(`[data-unit="${focusedUnitId}"]`);
      if (refocus && typeof refocus.focus === 'function') refocus.focus();
    }
  }

  renderArrangeUI() {
    const units = this.desiredUnits();

    const hint = this.querySelector('[data-arrange-hint]');
    if (hint) {
      hint.hidden = units.length === 0;
      const coarse =
        typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
      hint.textContent = coarse
        ? 'Select a display, then choose Left, Center, or Right. Arrangement is visual only.'
        : 'Drag displays to arrange your panel, or select one and use the position buttons. Arrangement is visual only.';
    }

    const controls = this.querySelector('[data-arrange-controls]');
    if (!controls) return;

    const active = this.state.arrangeUnitId;
    controls.hidden = !active;
    if (!active) return;

    const label = controls.querySelector('[data-arrange-label]');
    if (label) label.textContent = `Move ${this.unitLabel(active)} to:`;

    const fromSlot = this.slotOfUnit(active);
    for (const button of controls.querySelectorAll('[data-arrange-slot]')) {
      const slot = button.dataset.arrangeSlot;
      button.disabled = slot === fromSlot;
      const occupant = this.state.layout[slot];
      button.setAttribute(
        'aria-label',
        occupant && occupant !== active
          ? `${capitalize(slot)} position — swap with ${this.unitLabel(occupant)}`
          : `Move to the ${slot} position`
      );
    }
  }
}

if (!customElements.get(TAG_NAME)) {
  customElements.define(TAG_NAME, NealG3xBuilder);
}
